import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {
	FlexColumn,
	FlexRow,
	IconTooltip,
	Modal,
	NumberField,
	Text,
	TextField,
	Toggle,
	useRichTextEditorFunctions,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {RichTextEditorDraftJsWrapper} from '../../forecast-app/shared/components/rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';
import {convertFromHtmlToDraftJs} from '../../forecast-app/shared/components/rich-text-editor-draftjs-wrapper/HtmlToDraftJsConverter';
import CreateDeliverableMutation from '../../mutations/deliverables/CreateDeliverableMutation';
import DeleteDeliverableMutation from '../../mutations/deliverables/DeleteDeliverableMutation';
import UpdateDeliverableMutation from '../../mutations/deliverables/UpdateDeliverableMutation';
import {CommitMutation} from '../../mutations/CommitMutation';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util, {
	CANNOT_UNSET_PRICE_ON_DELIVERABLE_WITH_LOCKED_REVENUE,
	CANNOT_UNSET_PRICE_ON_INVOICED_DELIVERABLE,
	DELIVERABLE_PRICE_BELOW_INVOICED_AMOUNT,
} from '../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {BUDGET_TYPE} from '../../constants';
import {BaselineItem, Deliverable, DeliverableEdges} from './BaselineTypes';

type DeliverableEditorModalProps = {
	closeModal: () => void;
	deliverables: DeliverableEdges;
	deliverable?: Deliverable;
	action: 'create' | 'edit';
	projectId: string;
	onSuccess?: (deliverable: Deliverable) => void;
	baselineItems?: BaselineItem[];
	budgetType?: string;
	/** The project's currency: its rate card's, falling back to the company's. */
	currency?: string;
};

const DeliverableEditorModal = ({
	closeModal,
	deliverables,
	deliverable,
	action,
	projectId,
	onSuccess,
	baselineItems,
	budgetType,
	currency,
}: DeliverableEditorModalProps): JSX.Element => {
	const intl = useIntl();
	const {editorRef, getEditorValueAsHtml} = useRichTextEditorFunctions();

	const [description, setDescription] = useState<string>(deliverable?.description || '');
	const [name, setName] = useState<string>(deliverable?.name || '');
	const [price, setPrice] = useState<number | undefined>(deliverable?.price ?? undefined);
	const [closeEditor, setCloseEditor] = useState(false);
	const [priceError, setPriceError] = useState<string | undefined>(undefined);

	// A fee of zero is still a fee, and 0 is falsy — hence an explicit null check. A truthiness test
	// would open the editor claiming a zero-priced Fixed Price Deliverable is Time & Materials.
	const [isFixedPrice, setIsFixedPrice] = useState<boolean>(deliverable?.price != null);
	const [fixedPriceError, setFixedPriceError] = useState<string | undefined>(undefined);

	const invoicedAmount = deliverable?.invoicedAmount ?? 0;
	const lockedRevenue = deliverable?.lockedRevenue ?? 0;

	const formatAmount = (amount: number) => intl.formatNumber(amount, {minimumFractionDigits: 2, maximumFractionDigits: 2});

	/**
	 * The two reasons a fixed fee can no longer be removed, each with the copy explaining it.
	 *
	 * One list serves both paths that need it — the disabled switch below, and the server's refusal
	 * in the error handler — so the same refusal cannot end up worded two different ways. Ordered
	 * locked-revenue first to match the order the server checks them in, so a Deliverable blocked
	 * for both reasons is told the same one either way.
	 *
	 * `applies` reads the boolean fields the server derives from the same rows it enforces the
	 * refusal against, not the displayed amounts: a zero-priced Deliverable can be invoiced for a
	 * zero amount, and gating on `invoicedAmount > 0` would leave the switch enabled right up until
	 * the save round-trips and the server refuses it anyway. The amounts stay in scope only for the
	 * message text below.
	 */
	const removalRefusals = [
		{
			code: CANNOT_UNSET_PRICE_ON_DELIVERABLE_WITH_LOCKED_REVENUE,
			applies: deliverable?.hasLockedRevenue ?? false,
			message: () =>
				intl.formatMessage(
					{id: 'deliverables.cannot_unset_price_on_deliverable_with_locked_revenue'},
					{lockedRevenue: formatAmount(lockedRevenue)}
				),
		},
		{
			code: CANNOT_UNSET_PRICE_ON_INVOICED_DELIVERABLE,
			applies: deliverable?.hasBeenInvoiced ?? false,
			message: () =>
				intl.formatMessage(
					{id: 'deliverables.cannot_unset_price_on_invoiced_deliverable'},
					{invoicedAmount: formatAmount(invoicedAmount)}
				),
		},
	];

	// Disabled rather than allowed-and-then-rejected, since both reasons are knowable up front. The
	// server still enforces them — this copy of the numbers can be stale — so the refusals are
	// handled in handleUpdateError as well.
	const fixedFeeRemovalBlockedReason =
		deliverable?.price == null ? undefined : removalRefusals.find(refusal => refusal.applies)?.message();

	const isPriceMissing = isFixedPrice && price === undefined;

	const handleFixedPriceChange = (checked: boolean) => {
		setFixedPriceError(undefined);
		setPriceError(undefined);
		setIsFixedPrice(checked);
	};

	// Clearing on edit rather than on submit: the message names the amount the price has to clear,
	// so it stays visible while the user types the corrected figure and only goes once they act.
	const handlePriceChange = (value: number) => {
		setPriceError(undefined);
		setPrice(Number.isNaN(value) ? undefined : value);
	};

	const hasFixedFeeDeliverablesOnTmFeature = hasFeatureFlag('fixed_fee_deliverables_on_t&m');
	const isTimeAndMaterialsProject = budgetType === BUDGET_TYPE.TIME_AND_MATERIALS;

	const isCreate = action === 'create';
	const modalTitle = isCreate
		? intl.formatMessage({id: 'deliverables.edit.modal.add_new_deliverable'})
		: intl.formatMessage({id: 'deliverables.edit.modal.edit_deliverable'});
	const buttonText = isCreate
		? intl.formatMessage({id: 'deliverables.edit.modal.add_deliverable'})
		: intl.formatMessage({id: 'deliverables.edit.modal.save_changes'});

	/* Edit Deliverable */

	const onUpdateSuccess = response => {
		closeModal();
		const updatedDeliverable = response?.updateDeliverable?.deliverable?.node;
		const updatedDeliverables = deliverables.map(deliverable =>
			deliverable?.node?.id === updatedDeliverable.id ? {node: updatedDeliverable} : deliverable
		);
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: updatedDeliverables,
			projectId,
			baselineItems,
			budgetType,
			currency,
		});
	};

	/**
	 * Shows the price floor refusal against the price field rather than in a modal: the user has to
	 * change that specific number, so the message belongs where they will fix it, and the editor
	 * stays open with their other edits intact. Everything else is handed back to the shared
	 * handler, which owns retries and the generic error modal.
	 */
	const handleUpdateError = (transaction: Error, onFailure: (transaction: Error) => void) => {
		const refusal = removalRefusals.find(candidate => String(transaction).includes(`${candidate.code}:`));
		if (refusal) {
			setFixedPriceError(refusal.message());
			// Put the switch back where the server says it is, so the form stops claiming a change
			// that did not happen.
			setIsFixedPrice(true);
			return;
		}
		if (String(transaction).includes(`${DELIVERABLE_PRICE_BELOW_INVOICED_AMOUNT}:`)) {
			setPriceError(
				intl.formatMessage(
					{id: 'deliverables.price_below_invoiced_amount'},
					{
						invoicedAmount: intl.formatNumber(deliverable?.invoicedAmount ?? 0, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						}),
					}
				)
			);
			return;
		}
		onFailure(transaction);
	};

	/**
	 * What this save says about the fixed fee. Removing one needs an explicit instruction because an
	 * absent price means "leave the persisted fee alone" — so a Deliverable switched off Fixed Price
	 * sends `clearPrice`, one switched on sends its price, and one that was already Time & Materials
	 * says nothing at all.
	 *
	 * Create uses this too, and needs no branch of its own: there is no `deliverable`, so the
	 * off-branch reads `undefined != null` and returns nothing — never `clearPrice` against a
	 * Deliverable that does not exist yet.
	 */
	const fixedFeePayload = () => {
		if (isFixedPrice) {
			return price !== undefined ? {price} : {};
		}
		return deliverable?.price != null ? {clearPrice: true} : {};
	};

	const handleSave = useCallback(() => {
		if (!deliverable?.id) {
			return;
		}
		const descriptionPayload = convertFromHtmlToDraftJs(getEditorValueAsHtml());
		CommitMutation(
			UpdateDeliverableMutation,
			{
				id: String(deliverable.id),
				name,
				description: descriptionPayload,
				projectId,
				...(hasFixedFeeDeliverablesOnTmFeature ? fixedFeePayload() : {}),
			},
			onUpdateSuccess,
			true,
			null,
			handleUpdateError
		);
	}, [
		name,
		price,
		isFixedPrice,
		hasFixedFeeDeliverablesOnTmFeature,
		deliverable?.id,
		deliverable?.invoicedAmount,
		deliverable?.lockedRevenue,
		deliverable?.price,
		projectId,
		getEditorValueAsHtml,
	]);

	/* Create Deliverable */

	const onCreateSuccess = response => {
		closeModal();
		const deliverable = response?.createDeliverable?.deliverable?.node;
		if (onSuccess && deliverable) {
			return onSuccess(deliverable);
		}
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: [...deliverables, {node: deliverable}],
			projectId,
			baselineItems,
			budgetType,
			currency,
		});
	};

	const handleCreate = useCallback(() => {
		const descriptionPayload = convertFromHtmlToDraftJs(getEditorValueAsHtml());
		CommitMutation(
			CreateDeliverableMutation,
			{
				name,
				description: descriptionPayload,
				projectId,
				...(hasFixedFeeDeliverablesOnTmFeature ? fixedFeePayload() : {}),
			},
			onCreateSuccess
		);
		// `isFixedPrice` is a dependency, not decoration: switching off deliberately leaves the
		// entered price in state, so a callback memoised over a stale switch position would create a
		// Fixed Price Deliverable out of a form that reads Time & Materials.
	}, [name, price, isFixedPrice, hasFixedFeeDeliverablesOnTmFeature, projectId, getEditorValueAsHtml]);

	/* Delete Deliverable */

	const deliverablesWithEstimates = useMemo(
		() => new Set(baselineItems ? baselineItems.map(item => item.deliverableId) : []),
		[baselineItems]
	);

	const onDeleteSuccess = response => {
		setCloseEditor(true);
		closeModal();
		const deletedDeliverableId = response?.deleteDeliverable?.deletedDeliverableId;
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: deliverables.filter(deliverable => deliverable?.node?.id !== deletedDeliverableId),
			projectId,
			baselineItems,
			budgetType,
			currency,
		});
	};

	const handleDelete = () => {
		deliverable?.id && Util.CommitMutation(DeleteDeliverableMutation, {id: deliverable.id, projectId}, onDeleteSuccess);
	};

	const deleteConfirmation = () => {
		showModal({
			type: MODAL_TYPE.DELETE_DELIVERABLE_CONFIRMATION_MODAL,
			handleDelete,
			hasEstimates:
				baselineItems === undefined
					? 'unknown'
					: Boolean(deliverable?.id && deliverablesWithEstimates.has(deliverable.id)),
		});
	};

	useEffect(() => {
		if (closeEditor) {
			closeModal();
		}
	}, [closeEditor]);

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>{modalTitle}</Modal.Title>
			<Modal.Content>
				<TextField
					label={intl.formatMessage({id: 'deliverables.manage.modal.deliverable_name'})}
					value={name}
					onValueChange={setName}
					size="large"
					width="flex"
				/>
				{hasFixedFeeDeliverablesOnTmFeature && isTimeAndMaterialsProject && (
					<FlexColumn>
						<FlexRow gap="s">
							<Toggle
								checked={isFixedPrice}
								onChange={handleFixedPriceChange}
								disabled={fixedFeeRemovalBlockedReason !== undefined}
								error={fixedPriceError !== undefined}
								cy="fixed-price-toggle"
							>
								{intl.formatMessage({id: 'deliverables.edit.modal.mark_as_fixed_price'})}
							</Toggle>
							{/*
							 * Beside the Toggle rather than among its children: Toggle wraps whatever it
							 * is given in a <label> bound to the switch, and IconTooltip's trigger is a
							 * button — nested there, opening the tooltip would also flip the
							 * Deliverable's billing type. The cost is that the icon sits to the right of
							 * the switch instead of next to the words it explains.
							 */}
							<IconTooltip width={IconTooltip.WIDTH.l}>
								<IconTooltip.Item
									title={intl.formatMessage({id: 'deliverables.edit.modal.mark_as_fixed_price'})}
									details={intl.formatMessage({
										id: 'deliverables.edit.modal.mark_as_fixed_price_tooltip',
									})}
								/>
							</IconTooltip>
						</FlexRow>
						{(fixedPriceError ?? fixedFeeRemovalBlockedReason) && (
							<Text>{fixedPriceError ?? fixedFeeRemovalBlockedReason}</Text>
						)}
					</FlexColumn>
				)}
				{hasFixedFeeDeliverablesOnTmFeature && isFixedPrice && isTimeAndMaterialsProject && (
					<NumberField
						label={intl.formatMessage({id: 'deliverables.edit.modal.price'})}
						data-cy="deliverable-price"
						// NaN, not undefined, for "no price": react-stately reads an undefined value as
						// uncontrolled and would fall back to the price the field was opened with.
						value={price ?? Number.NaN}
						onChange={handlePriceChange}
						// A currency format without a currency code throws at render.
						format={currency ? NumberField.FORMAT.CURRENCY : undefined}
						currencyCode={currency}
						// Emptying the field must stay "no price" so a price remains required while the
						// switch is on; without this the field would quietly substitute 0.
						allowEmpty
						width={NumberField.WIDTH.FIXED}
						minValue={0}
						minimumFractionDigits={2}
						maximumFractionDigits={2}
						hasError={priceError !== undefined}
						errorText={
							priceError ??
							(isPriceMissing
								? intl.formatMessage({id: 'deliverables.edit.modal.price_required_for_fixed_price'})
								: undefined)
						}
					/>
				)}
				<FlexColumn>
					<FlexRow>{intl.formatMessage({id: 'deliverables.edit.modal.description_optional'})}</FlexRow>
					<RichTextEditorDraftJsWrapper
						data-cy="deliverable-description"
						editorRef={editorRef}
						initialDraftJsContent={description || ''}
						maxHeight="40vh"
						onDraftJsChanged={setDescription}
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					disabled={name === '' || isPriceMissing}
					onPress={isCreate ? handleCreate : handleSave}
					dataCy="action"
				>
					<FormattedMessage id={buttonText} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				{!isCreate && (
					<Modal.TertiaryFooterButton onPress={deleteConfirmation}>
						{intl.formatMessage({id: 'deliverables.edit.modal.delete_deliverable'})}{' '}
					</Modal.TertiaryFooterButton>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default DeliverableEditorModal;
